import { BASE_URL } from 'constants/env';
import useSWR from 'swr';
import type { CmsTravelThemeModifySchema } from 'typings/travel-theme/TravelThemeModifySchema';
import type { TravelThemeSchema } from 'typings/travel-theme/TravelThemeSchema';
import type { TravelThemeTranslationModifySchema } from 'typings/travel-theme/TravelThemeTranslationModifySchema';
import type { TravelThemeTranslationSchema } from 'typings/travel-theme/TravelThemeTranslationSchema';
import { get, post, patch, postDelete } from 'utils/http';

const defaultOptions = {
    headers: {
        'Polarsteps-Api-Version': '70',
    },
};

const TRAVEL_THEME_BASE_URL = `${BASE_URL}/cms/travel-themes`;

export async function getTravelThemes() {
    return get<TravelThemeSchema[]>(`${TRAVEL_THEME_BASE_URL}`, defaultOptions);
}

export async function getTravelTheme(themeId: number) {
    return get<TravelThemeSchema>(`${TRAVEL_THEME_BASE_URL}/${themeId}`, defaultOptions);
}

export async function createTravelTheme(body: CmsTravelThemeModifySchema) {
    return post<TravelThemeSchema>(`${TRAVEL_THEME_BASE_URL}`, body, defaultOptions);
}

export async function updateTravelTheme(themeId: number, body: CmsTravelThemeModifySchema) {
    return patch<TravelThemeSchema>(`${TRAVEL_THEME_BASE_URL}/${themeId}`, body, defaultOptions);
}

export async function deleteTravelTheme(themeId: number) {
    return postDelete(`${TRAVEL_THEME_BASE_URL}/${themeId}`, defaultOptions);
}

type SaveTravelThemeParams = {
    themeId?: number;
    body: CmsTravelThemeModifySchema;
    translations: Record<string, string>;
};

async function syncTravelThemeTranslations(savedTheme: TravelThemeSchema, translations: Record<string, string>) {
    const existingTranslations = savedTheme.translations ?? [];
    const requestedLanguages = new Set([
        ...Object.keys(translations),
        ...existingTranslations.map((translation) => translation.language),
    ]);
    requestedLanguages.delete('en');

    const promises: Promise<TravelThemeTranslationSchema | Response>[] = [];

    for (const language of requestedLanguages) {
        const newName = translations[language]?.trim() ?? '';
        const existing = existingTranslations.find((translation) => translation.language === language);

        if (newName && existing) {
            if (existing.name !== newName) {
                promises.push(
                    updateTravelThemeTranslation(existing.id, {
                        language,
                        name: newName,
                    })
                );
            }
        } else if (newName && !existing) {
            promises.push(
                createTravelThemeTranslation(savedTheme.id, {
                    language,
                    name: newName,
                })
            );
        } else if (!newName && existing) {
            promises.push(deleteTravelThemeTranslation(existing.id));
        }
    }

    await Promise.all(promises);
}

export function useTravelThemes() {
    const { data, isValidating, error, mutate } = useSWR('travel_themes', () => getTravelThemes(), {
        revalidateOnFocus: false,
    });

    const saveTheme = async ({ themeId, body, translations }: SaveTravelThemeParams) => {
        const savedTheme = themeId ? await updateTravelTheme(themeId, body) : await createTravelTheme(body);

        await syncTravelThemeTranslations(savedTheme, translations);
        await mutate();
        return savedTheme;
    };

    const removeTheme = async (themeId: number) => {
        await deleteTravelTheme(themeId);
        await mutate();
    };

    return {
        themes: data ?? [],
        isLoading: isValidating && !data,
        error,
        mutate,
        saveTheme,
        removeTheme,
    };
}

export async function createTravelThemeTranslation(themeId: number, body: TravelThemeTranslationModifySchema) {
    return post<TravelThemeTranslationSchema>(`${TRAVEL_THEME_BASE_URL}/${themeId}/translations`, body, defaultOptions);
}

export async function updateTravelThemeTranslation(translationId: number, body: TravelThemeTranslationModifySchema) {
    return patch<TravelThemeTranslationSchema>(
        `${TRAVEL_THEME_BASE_URL}/translations/${translationId}`,
        body,
        defaultOptions
    );
}

export async function deleteTravelThemeTranslation(translationId: number) {
    return postDelete(`${TRAVEL_THEME_BASE_URL}/translations/${translationId}`, defaultOptions);
}
